import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { ConcentricRings } from '@/components/Motifs';
import { CheckCircle, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/usePageTitle';
import api from '@/lib/api';

export default function CheckoutSuccessPage() {
  usePageTitle('Order Confirmed');
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    const poll = async (attempt = 0) => {
      if (attempt >= 8) {
        setStatus('timeout');
        return;
      }
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('paid');
          setPaymentData(res.data);
        } else if (res.data.status === 'expired') {
          setStatus('expired');
        } else {
          setStatus('processing');
          setTimeout(() => poll(attempt + 1), 2500);
        }
      } catch (err) {
        setStatus('error');
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Determine product type from URL params or metadata
  const productSlug = searchParams.get('product') || '';
  const isPlanner = productSlug === 'prism-planner' || paymentData?.metadata?.product_slug === 'prism-planner';
  const isService = productSlug === 'idea-clarity-session' || paymentData?.metadata?.product_slug === 'idea-clarity-session';

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <section className="py-20 sm:py-32">
        <div className="max-w-lg mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            {status === 'loading' || status === 'processing' ? (
              <>
                <Loader2 className="h-12 w-12 text-[hsl(var(--gold))] mx-auto mb-6 animate-spin" />
                <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-4">
                  Processing Payment
                </h1>
                <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">
                  Please wait while we confirm your payment...
                </p>
              </>
            ) : status === 'paid' ? (
              <>
                <CheckCircle className="h-16 w-16 text-[hsl(var(--sage))] mx-auto mb-6" />
                <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-4">
                  Payment Successful
                </h1>
                <p className="font-['Inter'] text-base text-[hsl(var(--muted-foreground))] mb-8">
                  Thank you for your purchase. A confirmation has been sent to your email.
                </p>

                {/* Product-specific content */}
                {isPlanner && (
                  <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.25)] p-6 sm:p-8 mb-8">
                    <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-3">
                      Your Access
                    </p>
                    <p className="font-['Cormorant_Garamond'] text-2xl font-bold text-[hsl(var(--foreground))] mb-4">
                      Prism Planner
                    </p>
                    <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] mb-6">
                      Your lifetime access is ready. Create an account or sign in to get started.
                    </p>
                    <a
                      href="https://planner.thecosmicsynthesist.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-8 py-5 rounded-lg hover:brightness-[1.03]">
                        Open Prism Planner
                        <ExternalLink className="h-4 w-4 ml-2" />
                      </Button>
                    </a>
                  </div>
                )}

                {isService && (
                  <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.25)] p-6 sm:p-8 mb-8">
                    <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-3">
                      What Happens Next
                    </p>
                    <p className="font-['Cormorant_Garamond'] text-2xl font-bold text-[hsl(var(--foreground))] mb-4">
                      The Idea Clarity Session
                    </p>
                    <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] mb-4">
                      Emma will reach out within 24 hours to schedule your 90-minute session. Check your inbox for a confirmation and scheduling details.
                    </p>
                    <p className="font-['Inter'] text-xs text-[hsl(var(--silver))]">
                      Questions? Email{' '}
                      <a href="mailto:emma@thecosmicsynthesist.com" className="text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))]">
                        emma@thecosmicsynthesist.com
                      </a>
                    </p>
                  </div>
                )}

                {!isPlanner && !isService && (
                  <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.25)] p-6 sm:p-8 mb-8">
                    <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">
                      You will receive a confirmation email with details about your purchase.
                    </p>
                  </div>
                )}

                <Link to="/">
                  <Button
                    variant="outline"
                    className="border-[rgba(201,168,76,0.28)] text-[hsl(var(--foreground))] font-['Inter'] text-sm font-medium px-8 py-5 rounded-lg"
                  >
                    Return Home
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <AlertCircle className="h-16 w-16 text-[hsl(var(--rose))] mx-auto mb-6" />
                <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-4">
                  {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
                </h1>
                <p className="font-['Inter'] text-base text-[hsl(var(--muted-foreground))] mb-8">
                  {status === 'expired'
                    ? 'Your payment session has expired. Please try again.'
                    : 'We could not confirm your payment. Please contact us if you believe this is an error.'}
                </p>
                <Link to="/">
                  <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-8 py-5 rounded-lg">
                    Return Home
                  </Button>
                </Link>
              </>
            )}
          </motion.div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
